import { forwardRef, CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { HTMLMotionProps, motion } from 'motion/react';
import type { ModalBoxProps } from '../atoms/modal-box';
import { useModalContext } from '../provider/modal.provider';
import { ResponsiveModalBox } from './responsive-modal-box';
import { useResponsiveModal } from './responsive-modal.context';
import { responsiveModalBodyResponsive } from './responsive-modal.styles';

export type ResponsiveModalBodyProps = ModalBoxProps;

/**
 * Equivalent to `CenteredModalBox` when !isResponsiveMode (inside <Modal.Responsive>)
 * Otherwise, equivalent to full screen / mobile modal.
 */
export const ResponsiveModalBody = forwardRef<HTMLElement, ResponsiveModalBodyProps>((props, ref) => {
  const { isResponsiveMode, topOffset } = useResponsiveModal();
  const { containerProps } = useModalContext();

  const motionStyles = getBodyMotionStyles({ isResponsiveMode });
  const bodyContainerProps = isResponsiveMode ? containerProps : {};

  return (
    <motion.section
      className={cx('responsive-modal__body', isResponsiveMode && responsiveModalBodyResponsive)}
      {...motionStyles}
      {...bodyContainerProps}
      style={isResponsiveMode ? ({ '--responsive-modal-top-offset': `${topOffset}px` } as CSSProperties) : undefined}
      ref={ref}
    >
      <ResponsiveModalBox {...props} />
    </motion.section>
  );
});

ResponsiveModalBody.displayName = 'ResponsiveModalBody';

const getBodyMotionStyles = ({ isResponsiveMode }: { isResponsiveMode: boolean }): HTMLMotionProps<'section'> => {
  if (isResponsiveMode) {
    return {
      variants: {
        hidden: {
          y: '100%',
        },
        visible: {
          y: '0%',
        },
        exit: {
          y: '100%',
        },
      },
      initial: 'hidden',
      animate: 'visible',
      exit: 'exit',
      transition: { type: 'keyframes', stiffness: 260, damping: 20 },
    };
  }

  return {
    initial: {
      opacity: 0,
      y: '50%',
    },
    animate: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: '50%',
    },
    transition: {
      type: 'spring',
      damping: 24,
      stiffness: 300,
    },
  };
};
